import { DateTime } from "luxon";
import { AccessLevel, CommonBaseModel, ProductWorkflowState } from "./common.model";
import { Pipe, PipeTransform, signal } from "@angular/core";
import { TruthCondition } from "./truth-condition.model";
import { ItemState, ServiceEndpointUtilisation } from "./workflow.model";
import { iApplyFormControl } from "./control.model";
import { tryParseDate } from "../global.functions";
import { ControlFactory } from "./control-factory";

export enum NavigationStyle {
    None = 0, //"None",
    Page = 1, // "Page",
    FullScreen = 2 //"FullScreen",
}

// Represents a set of information about a specific form.
export class FormDescription extends CommonBaseModel {
    // Gets or sets a value indicating whether the form has any scoring controls.
    hasScoringControls: boolean; // ToDo need to connect
    // Gets or sets a list of referenced controls.
    referencedControls: iApplyFormControl[] = [];

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);
        this.referencedControls = [];
        let referencedControls = _data["referencedControls"];
        if (Array.isArray(referencedControls)) {
            this.referencedControls = referencedControls.map(item => {
                let _item = ControlFactory.createControl(item);
                return _item;
            });
        }

        return this;
    }
}

export interface ProductData {
    success: boolean;
    metadata: ProductDefinition;
    externalPopulatedFields: string[];
}

/** Represents the metadata for a Form product. */
export class ProductMetadata extends CommonBaseModel {
    productId!: string;
    version!: number;
    name: string;
    description: string;
    tags: string[] = [];
    accessibleOffline!: boolean;
    activeDate!: DateTime;
    allowForwardNavigation!: boolean;
    allowPrepopulate!: boolean;
    allowSaving!: boolean;
    analytics!: boolean;
    applicationKey: string | undefined;
    copilotTokenEndpoint: string | undefined;
    copilotChatBubbleColor: string | undefined;
    copilotChatBubbleTextColor: string | undefined;
    bubbleFromUserBackground: string | undefined;
    bubbleFromUserTextColor: string | undefined;
    userAvatarBackgroundColor: string | undefined;
    bannerBackgroundColor: string | undefined;
    archived!: boolean;
    autoOpenLastVisitedPage!: boolean;
    branchName: string;
    childForms: { [key: string]: string; };
    closeButtonText: string;
    closeNavigationAddress: string;
    enableApplicationLog!: boolean;
    enableAuditLog!: boolean;
    enableDataTransformation!: boolean;
    enableParallaxEffect!: boolean;
    enableMicrosoftCopilot!: boolean;
    expiryDate: DateTime | undefined;
    formDescription: FormDescription;
    formVaultAccessible!: boolean;
    latestVersion!: boolean;
    linkApplicationsToVersion!: boolean;
    liveValidation!: boolean;
    modified: DateTime | undefined;
    modifiedBy: string;
    navigationStyle!: NavigationStyle; // menuDepth
    nonElectronicSubmission!: boolean;
    organisationId: string;
    parentFormId: string;
    previousVersion: number;
    publicAccess!: boolean;
    publishedVersion!: boolean;
    roles: string[] = [];
    showCloseButton!: boolean;
    themeName: string;
    workflowState!: ProductWorkflowState;

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);

        super.initArray(this, "roles", _data["Roles"]);
        super.initArray(this, "tags", _data["Categories"]);
        this.activeDate = tryParseDate(_data["ActiveDate"]);
        super.initArray(this, "childForms", _data["ChildForms"]);
        this.expiryDate = tryParseDate(_data["ExpiryDate"]);

        this.formDescription = _data["FormDescription"] ? (new FormDescription()).init(_data["FormDescription"]) : <any>undefined; //ToDo update
        this.modified = tryParseDate(_data["LastModified"]);
        this.navigationStyle = _data["MenuDepth"];

        return this;
    }
}

export class ProductDefinitionArray extends CommonBaseModel {
    products: ProductDefinition[] = []

    init(_data?: any): this {
        if (!_data) {
            return this;
        }

        _data = _data["response"] ? _data["response"] : _data;
        if (!Array.isArray(_data)) {
            return this;
        }

        for (let item of _data) {
            if (!item) {
                continue;
            }

            this.products!.push(new ProductDefinition().init(item));
        }

        return this;
    }
}

/** A product with form pages. */
export class ProductDefinition extends ProductMetadata {
    /** Gets or sets the form pages. */
    pages: FormPage[] = [];
    /** Gets or sets the current page identifier.
        This is an integer that is incremented each new page that is added to this product. */
    currentPageId!: number;
    /** Gets or sets the current control identifier.
        This is an integer that is incremented each new control that is added to any page in this product. */
    currentControlId!: number;

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);
        if (_data["Form"] && Array.isArray(_data["Form"]["Pages"])) {
            this.pages = [];
            for (let item of _data["Form"]["Pages"]) {
                this.pages!.push(FormPage.getInitedPageDependingOnPageType(item));
            }
        }

        return this;
    }
}

/**
 * FORM PAGES
 */

export function isUserFormPage(page: any): page is UserFormPage {
    return page
        && page.pageType == FormPageType.User;
    // && has(page, 'submitsApplication')
    // && has(page, 'submitWorkflowStates');
}

@Pipe({
    name: 'isUserFormPage',
    standalone: true,
})
export class IsUserFormPagePipe implements PipeTransform {
    transform(object: object): boolean {
        return isUserFormPage(object);
    }
}

export class FormPage extends CommonBaseModel {
    id!: string | undefined;
    /** Gets or sets the page title. */
    pageTitle!: string | undefined;
    /** Gets or sets the menu title. */
    menuTitle!: string | undefined;
    /** Gets or sets the internal page number. */
    pageId!: number;
    pageType!: FormPageType;
    /** Gets or sets the sort order. */
    sortOrder!: number;
    /** Gets or sets the parent page identifier. */
    // parentPageId!: number | undefined;
    /** Gets or sets the list of controls for this page. */
    controls!: iApplyFormControl[] | undefined;
    /** Gets or sets the notes. */
    // notes!: string | undefined;
    /** Gets or sets the form identifier. */
    productId!: string | undefined;
    /** Gets or sets the form version. */
    productVersion!: number;

    // CUSTOM
    pageIndex: number; // used in navigation

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);

        // super.initArray(this, "controls", _data["Controls"]); // not plain array...
        if (Array.isArray(_data["Controls"])) {
            this.controls = _data['Controls'].map(iApplyFormControl.fromJS);
        }

        return this;
    }

    static getInitedPageDependingOnPageType(data: any): FormPage {
        data = typeof data === 'object' ? data : {};
        let result = null;

        switch (data['PageType'] || data['Pagetype']) {
            case FormPageType.User:
                result = new UserFormPage();
                break;

            case FormPageType.Receipt:
                result = new FormPage();
                break;

            default:
                result = new UserFormPage();
        }

        result.init(data);
        return result;
    }

    // toJSON(data?: any) {
    //     data = typeof data === 'object' ? data : {};
    //     data["PageTitle"] = this.pageTitle;
    //     data["MenuTitle"] = this.menuTitle;
    //     data["PageId"] = this.pageId;
    //     data["PageType"] = this.pageType;
    //     data["SortOrder"] = this.sortPosition;
    //     data["ParentPageId"] = this.parentPageId;
    //     if (Array.isArray(this.controls)) {
    //         data["Controls"] = [];
    //         for (let item of this.controls)
    //             data["Controls"].push(item.toJSON());
    //     }
    //     data["Notes"] = this.notes;
    //     data["ProductId"] = this.formId;
    //     data["ProductVersion"] = this.formVersion;
    //     data["_id"] = { "$oid": this.id };
    //     return data;
    // }
}

export class UserFormPage extends FormPage {
    subPage: boolean = false;
    submitsApplication: boolean = false;
    submitLabel: string = 'Submit';
    submitWorkflowStates: ItemState[] = [];
    rules: TruthCondition[] = [];
    externalRuleHandlers: ServiceEndpointUtilisation[] = [];

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);
        super.initArray(this, 'submitWorkflowStates', _data["SubmitWorkflowStates"]);
        super.initArray(this, 'rules', _data["Rules"]);

        return this;
    }
}

/** Represents the types of form page. */
export enum FormPageType {
    User = "user",
    Receipt = "receipt",
}

/**
 * ENTITLEMENTS
 */

/** Represents a metadata entitlement setting. */
export class PageEntitlement implements IPageEntitlement {
    /** Gets or sets the page ID. */
    pageId!: number;
    accessLevel!: AccessLevel;
    /** Gets or sets the product ID. */
    formId!: string | undefined;
    /** Gets or sets the form version. */
    formVersion!: number;
    /** Gets or sets the role ID. */
    roleId!: string | undefined;
    /** Gets or sets the state name. */
    stateName!: string | undefined;
    id!: string | undefined;

    constructor(data?: IPageEntitlement) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.pageId = _data["pageId"];
            this.accessLevel = _data["accessLevel"];
            this.formId = _data["formId"];
            this.formVersion = _data["formVersion"];
            this.roleId = _data["roleId"];
            this.stateName = _data["stateName"];
            this.id = _data["id"];
        }
    }

    static fromJS(data: any): PageEntitlement {
        data = typeof data === 'object' ? data : {};
        let result = new PageEntitlement();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["pageId"] = this.pageId;
        data["accessLevel"] = this.accessLevel;
        data["formId"] = this.formId;
        data["formVersion"] = this.formVersion;
        data["roleId"] = this.roleId;
        data["stateName"] = this.stateName;
        data["id"] = this.id;
        return data;
    }
}

/** Represents a metadata entitlement setting. */
export interface IPageEntitlement {
    /** Gets or sets the page ID. */
    pageId: number;
    accessLevel: AccessLevel;
    /** Gets or sets the product ID. */
    formId: string | undefined;
    /** Gets or sets the form version. */
    formVersion: number;
    /** Gets or sets the role ID. */
    roleId: string | undefined;
    /** Gets or sets the state name. */
    stateName: string | undefined;
    id: string | undefined;
}

export interface IDataChangedEvent {
    key: string;
    // oldValue: any;
    newValue: any;
}

export class SurveyResult extends CommonBaseModel {
    totalScore: number;
    totalScoreAvailable: number;
    totalScorePercentage: number;

    init(_data?: any): this {
        super.init(_data);
        return this;
    }

    static fromJS(data: any): SurveyResult {
        data = typeof data === 'object' ? data : {};
        let result = new SurveyResult();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["totalScore"] = this.totalScore;
        data["totalScoreAvailable"] = this.totalScoreAvailable;
        data["totalScorePercentage"] = this.totalScorePercentage;

        return data;
    }
}